<template>
  <div>
    <div class="d-flex justify-content-between ts-mb-3">
      <h1 class="page-header ts-mb-0">{{ $t('bankV2.pageTitle') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('toHomePage'), href: '/' },
          { text: $t('bankV2.breadcrumb'), active: true }
        ]"
      />
    </div>

    <ts-card>
      <ts-card-wrapper>
        <div
          class="ts-flex ts-flex-wrap ts-items-center ts-justify-between ts-gap-3"
        >
          <ts-button
            v-if="can('create-bank-profiles')"
            color="info"
            outline
            @click="addNew"
          >
            {{ $t('bankV2.addNew') }}
          </ts-button>
          <div v-else></div>

          <div class="ts-flex ts-flex-wrap ts-items-center ts-gap-2">
            <a-input-search
              v-model="search"
              size="small"
              style="width: 330px"
              :placeholder="$t('bankV2.search')"
              @search="fetchData"
            />
            <a-button
              class="filter-button"
              icon="filter"
              @click="openFilterDrawer"
            >
              {{ $t('bankV2.filter') }}
            </a-button>
          </div>
        </div>
      </ts-card-wrapper>

      <div class="overflow-auto ts-mt-3">
        <Table
          :columns="columns"
          :data="resources"
          :loading="loading"
          max-height="700"
          stripe
          highlight-row
          size="small"
        >
          <template slot-scope="{ row }" slot="logo">
            <div class="bank-logo-cell">
              <img
                v-if="row.logo_url"
                :src="row.logo_url"
                :alt="row.bank_name || $t('bankV2.logo')"
              />
              <i v-else class="fas fa-university"></i>
            </div>
          </template>

          <template slot-scope="{ row }" slot="bank">
            <div class="ts-font-semibold">{{ row.bank_name }}</div>
            <div class="ts-text-xs ts-text-gray-500">
              {{
                [row.short_name, row.bank_name_kh]
                  .filter(Boolean)
                  .join(' / ') || '-'
              }}
            </div>
          </template>

          <template slot-scope="{ row }" slot="branch">
            {{ row.branch_name_en || '-' }}
          </template>

          <template slot-scope="{ row }" slot="status">
            <span
              class="ts-inline-flex ts-items-center ts-rounded ts-px-2 ts-py-1 ts-text-xs"
              :class="
                row.is_active
                  ? 'ts-bg-green-100 ts-text-green-700'
                  : 'ts-bg-gray-100 ts-text-gray-600'
              "
            >
              {{ row.is_active ? $t('bankV2.active') : $t('bankV2.inactive') }}
            </span>
          </template>

          <template slot-scope="{ row }" slot="khqr">
            {{ row.khqr_provider ? row.khqr_provider.toUpperCase() : '-' }}
          </template>

          <template slot-scope="{ row }" slot="action">
            <div class="ts-flex ts-space-x-2 ts-items-center ts-justify-center">
              <a
                v-if="can('update-bank-profiles')"
                href="#"
                v-tooltip="$t('edit')"
                class="text-primary ts-h-6 ts-w-6 ts-flex ts-items-center ts-justify-center ts-bg-blue-100 ts-rounded hover:ts-no-underline"
                @click.prevent="edit(row)"
              >
                <i class="fas fa-pen"></i>
              </a>
              <a-popconfirm
                v-if="can('delete-bank-profiles')"
                placement="topRight"
                :ok-text="$t('bankV2.yes')"
                :cancel-text="$t('bankV2.no')"
                @confirm="deleteRecord(row)"
              >
                <template slot="title">
                  <p>{{ $t('bankV2.confirmDelete') }}</p>
                </template>
                <a
                  href="#"
                  v-tooltip="$t('delete')"
                  class="ts-text-red-500 ts-h-6 ts-w-6 ts-flex ts-items-center ts-justify-center ts-bg-red-100 ts-rounded hover:ts-no-underline hover:ts-text-red-500"
                  @click.prevent
                >
                  <i class="fas fa-trash"></i>
                </a>
              </a-popconfirm>
            </div>
          </template>
        </Table>
      </div>

      <div class="d-flex justify-content-between align-items-center p-15">
        <ts-total-list v-model="pagination" />
        <ts-pagination v-model="pagination" @navigate="fetchData" />
      </div>
    </ts-card>

    <a-drawer
      :visible="filterDrawerVisible"
      :title="$t('bankV2.filter')"
      width="360"
      placement="right"
      :zIndex="1021"
      @close="closeFilterDrawer"
    >
      <div class="filter-drawer-body">
        <div class="filter-field">
          <label>{{ $t('bankV2.branch') }}</label>
          <a-select
            v-model="filterForm.branch_ids"
            class="ts-w-full"
            mode="multiple"
            :placeholder="$t('bankV2.allBranches')"
            allow-clear
          >
            <a-select-option
              v-for="branch in options.branches"
              :key="branch.branch_id"
              :value="branch.branch_id"
            >
              {{ branch.branch_name_en || branch.branch_name_kh }}
            </a-select-option>
          </a-select>
        </div>
        <div class="filter-field">
          <label>{{ $t('bankV2.status') }}</label>
          <a-select
            v-model="filterForm.status"
            class="ts-w-full"
            :placeholder="$t('bankV2.allStatus')"
            allow-clear
          >
            <a-select-option :value="1">
              {{ $t('bankV2.active') }}
            </a-select-option>
            <a-select-option :value="0">
              {{ $t('bankV2.inactive') }}
            </a-select-option>
          </a-select>
        </div>
      </div>
      <div class="filter-drawer-footer">
        <a-button @click="clearFilter">{{ $t('bankV2.clearFilter') }}</a-button>
        <a-button type="primary" @click="applyFilter">
          {{ $t('bankV2.applyFilter') }}
        </a-button>
      </div>
    </a-drawer>

    <a-modal
      :visible="showForm"
      :footer="null"
      centered
      width="980px"
      :title="
        formMode === 'edit' ? $t('bankV2.editTitle') : $t('bankV2.createTitle')
      "
      :destroy-on-close="true"
      @cancel="closeForm"
      :zIndex="900"
    >
      <bank-form
        v-if="showForm"
        :mode="formMode"
        :bank-id="editId"
        variant="modal"
        @saved="onFormSaved"
        @cancel="closeForm"
      />
    </a-modal>
  </div>
</template>

<script>
import notificationAlert from '@/utils/NotificationAlert'
import { mapActions, mapState } from 'vuex'
import BankForm from '../components/BankForm.vue'

export default {
  name: 'BankV2Index',
  components: { BankForm },
  data() {
    return {
      loading: false,
      showForm: false,
      formMode: 'create',
      editId: null,
      filterDrawerVisible: false,
      filterForm: {
        branch_ids: [],
        status: undefined
      },
      filters: {
        branch_ids: [],
        status: undefined
      }
    }
  },
  computed: {
    ...mapState('bankV2', ['resources', 'pagination', 'options']),
    search: {
      get() {
        return this.$store.state.bankV2.search
      },
      set(value) {
        this.$store.commit('bankV2/SET_SEARCH', value)
        this.$store.commit('bankV2/RESET_CURRENT_PAGE')
      }
    },
    columns() {
      return [
        {
          title: this.$t('bankV2.logo'),
          slot: 'logo',
          width: 74,
          align: 'center'
        },
        { title: this.$t('bankV2.bankName'), slot: 'bank', minWidth: 220 },
        { title: this.$t('bankV2.branch'), slot: 'branch', minWidth: 170 },
        {
          title: this.$t('bankV2.accountCount'),
          key: 'account_count',
          width: 110,
          align: 'center'
        },
        {
          title: this.$t('bankV2.khqrProvider'),
          slot: 'khqr',
          width: 130,
          align: 'center'
        },
        {
          title: this.$t('bankV2.active'),
          slot: 'status',
          width: 110,
          align: 'center'
        },
        { title: this.$t('bankV2.createdAt'), key: 'created_date', width: 150 },
        {
          title: this.$t('bankV2.modifiedAt'),
          key: 'modified_date',
          width: 150
        },
        {
          title: this.$t('bankV2.actions'),
          slot: 'action',
          fixed: 'right',
          align: 'center',
          width: 100
        }
      ]
    }
  },
  mounted() {
    this.fetchOptions()
    this.fetchData()
  },
  methods: {
    ...mapActions('bankV2', ['fetch', 'fetchOptions', 'onDelete']),
    can(permission) {
      return this.$store.getters.authPermissionAllow(permission)
    },
    fetchData(attributes = {}) {
      this.loading = true
      return this.fetch(
        Object.assign(
          {
            branch_ids: this.filters.branch_ids,
            status: this.filters.status
          },
          attributes
        )
      )
        .catch(error => notificationAlert(error))
        .finally(() => {
          this.loading = false
        })
    },
    openFilterDrawer() {
      this.filterForm = {
        branch_ids: [...this.filters.branch_ids],
        status: this.filters.status
      }
      this.filterDrawerVisible = true
    },
    closeFilterDrawer() {
      this.filterDrawerVisible = false
    },
    applyFilter() {
      this.filters = {
        branch_ids: [...this.filterForm.branch_ids],
        status: this.filterForm.status
      }
      this.$store.commit('bankV2/RESET_CURRENT_PAGE')
      this.filterDrawerVisible = false
      this.fetchData()
    },
    clearFilter() {
      this.filterForm = {
        branch_ids: [],
        status: undefined
      }
      this.filters = {
        branch_ids: [],
        status: undefined
      }
      this.$store.commit('bankV2/RESET_CURRENT_PAGE')
      this.filterDrawerVisible = false
      this.fetchData()
    },
    addNew() {
      this.formMode = 'create'
      this.editId = null
      this.showForm = true
    },
    edit(row) {
      this.formMode = 'edit'
      this.editId = row.bank_id
      this.showForm = true
    },
    closeForm() {
      this.showForm = false
      this.editId = null
    },
    onFormSaved() {
      this.closeForm()
      this.fetchData()
    },
    deleteRecord(row) {
      this.loading = true
      this.onDelete(row.bank_id)
        .then(response => {
          notificationAlert(response, 'success')
          this.fetchData()
        })
        .catch(error => notificationAlert(error))
        .finally(() => {
          this.loading = false
        })
    }
  }
}
</script>

<style scoped>
.filter-drawer-body {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-bottom: 72px;
}

.filter-field label {
  color: #374151;
  display: block;
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 6px;
}

.filter-drawer-footer {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 16px;
  background: #ffffff;
  border-top: 1px solid #e5e7eb;
}

.bank-logo-cell {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: #f8fafc;
  color: #94a3b8;
}

.bank-logo-cell img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
</style>
